import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["id"]

import Spatial2DBoundingBox from "@/components/frame/image/annotation/Spatial2DBoundingBox.vue";
import {computed, onMounted, onUnmounted, PropType, reactive, Ref, ref, watch} from "vue";
import {BoundingBoxShortcut} from "@/components/frame/image/annotation/type/BoundingBoxShortcut";
import {BoundingBoxShortcutType} from "@/components/frame/image/annotation/enum/BoundingBoxShortcutType";
import ShortcutsManager, {SPECIAL_SHORTCUT_CODE} from "@/lib/common/helpers/ShortcutsManager";
import {ReactiveVariable} from "vue/macros";
import {SAnnotatableImageData} from "@/store/Spatial2DAnnotation/model/SAnnotatableImageData";
import {
  AnnotatableImagesStoreManager
} from "@/store/Spatial2DAnnotation/manager/AnnotatableImagesStoreManager";
import {
  SAnnotatableBodyPartDisplayParamsData
} from "@/store/Spatial2DAnnotation/model/SAnnotatableBodyPartDisplayParamsData";
import {
  Spatial2DBoundingBoxManager
} from "@/store/Spatial2DAnnotation/manager/subManager/Spatial2DBoundingBoxManager";
import {AbstractSubStoreManager} from "@/lib/store/manager/abstract/AbstractSubStoreManager";
import {SSpatial2DBoundingBox} from "@/store/Spatial2DAnnotation/model/SSpatial2DBoundingBox";
import {
  AnnotatableBodyPartDisplayParamsStoreManager
} from "@/store/Spatial2DAnnotation/manager/AnnotatableBodyPartDisplayParamsStoreManager";


export default /*#__PURE__*/_defineComponent({
  name: 'BoundingBoxOverlay',
  props: {
  annotatableImage: {
    type: Object as PropType<SAnnotatableImageData>,
    required: true
  }
},
  setup(__props) {

const props = __props




// ------------------------------
//  Overlay
// ------------------------------

const overlay = ref();

// ------------------------------
//  Bounding Box
// ------------------------------

const currentBoundingBox = computed(() => {
  return AnnotatableImagesStoreManager.store.currentBoundingBox;
})

const isBoundingBoxCurrent = computed(() => {
  return (instanceId: string) => {
    return !!(AnnotatableImagesStoreManager.store.currentBoundingBox && AnnotatableImagesStoreManager.store.currentBoundingBox.instanceId === instanceId)
  };
});

const setCurrentBoundingBox = (boundingBox: SSpatial2DBoundingBox) =>
{
  if (
      !currentBoundingBox.value
      || currentBoundingBox.value.instanceId !== boundingBox.instanceId
  )
    AnnotatableImagesStoreManager.setCurrentBoundingBox(boundingBox);
}

// ------------------------------
//  Mouse position tracking
// ------------------------------

const cursorOnOverlayX = ref(),
    cursorOnOverlayY = ref(),
    cursorTrackingByBoundingBox: ReactiveVariable<{ [instanceId: string]: boolean }> = reactive({});

onMounted(() =>
{
  overlay.value.addEventListener("mousemove", function(this: HTMLElement, event: MouseEvent)
  {
    const rect = this.getBoundingClientRect();

    cursorOnOverlayX.value = event.pageX - rect.left;
    cursorOnOverlayY.value = event.pageY - rect.top;
  });
})

const onCursorEntersCurrentBoundingBox = (instanceId: string) => {
  cursorTrackingByBoundingBox[instanceId] = true;
}

const onCursorLeavesCurrentBoundingBox = (instanceId: string) => {
  cursorTrackingByBoundingBox[instanceId] = false;
}

// ------------------------------
//  Shortcuts
// ------------------------------

const boundingBoxShortcuts: Ref<BoundingBoxShortcut[]> = computed(() =>
{
  const base = [
    {
      type: BoundingBoxShortcutType.SPAWN_BOUNDING_BOX,
      keyCode: "Space"
    },
    {
      type: BoundingBoxShortcutType.CLEAR_ALL,
      keyCode: "Escape"
    },
    {
      type: BoundingBoxShortcutType.SWITCH_TO_NEXT_SPAWNLABLE,
      keyCode: SPECIAL_SHORTCUT_CODE.WHEEL_DOWN
    },
    {
      type: BoundingBoxShortcutType.SWITCH_TO_PREVIOUS_SPAWNLABLE,
      keyCode: SPECIAL_SHORTCUT_CODE.WHEEL_UP
    },
  ];

  return [
    ...base,
  ];
});

let shortcutHolderInstanceIds: string[] = [];

onMounted(() =>
{
  registerShortcuts();
})

onUnmounted(() => {
  shortcutHolderInstanceIds.forEach(shortcutHolderInstanceId => ShortcutsManager.unregister(shortcutHolderInstanceId));
})

const registerShortcuts = () =>
{
  boundingBoxShortcuts.value.forEach((shortcut) =>
  {
    shortcutHolderInstanceIds.push(ShortcutsManager.register(shortcut.keyCode, shortcut.requiresCtrl as boolean, shortcut.requiresAlt as boolean, shortcut.requiresShift as boolean,  () =>
    {
      switch (shortcut.type)
      {
        case BoundingBoxShortcutType.SPAWN_BOUNDING_BOX:
          spawnEmptyBoundingBox();
          break;

        case BoundingBoxShortcutType.CLEAR_ALL:
          clearAll();
          break;

        case BoundingBoxShortcutType.SWITCH_TO_PREVIOUS_SPAWNLABLE:
          switchToPreviousSpawnableAnnotation();
          break;

        case BoundingBoxShortcutType.SWITCH_TO_NEXT_SPAWNLABLE:
          switchToNextSpawnableAnnotation();
          break;
      }
    }));
  })
}

// ------------------------------
//  Bounding box
// ------------------------------

const isCowImageCurrent = computed(() => {
  return AnnotatableImagesStoreManager.store.currentAnnotatableImage?.instanceId === props.annotatableImage.instanceId;
});

const spawnEmptyBoundingBox = () =>
{
  if (isCowImageCurrent.value)
    AnnotatableImagesStoreManager.spawnEmptyBoundingBox();
}

const clearAll = () =>
{
  if (isCowImageCurrent.value)
    AnnotatableImagesStoreManager.store.resetCurrentAnnotatableImage();
}

// ------------------------------
//  Resets the body part display params when the bounding box becomes current
// ------------------------------

watch(() => isCowImageCurrent.value, () =>
{
  if (isCowImageCurrent.value && currentBoundingBox.value)
  {
    AnnotatableBodyPartDisplayParamsStoreManager.resetBaseOnBoundingBox(currentBoundingBox.value);
  }
});

// ------------------------------
//  Spawnable Annotation
// ------------------------------

const switchToNextSpawnableAnnotation = () => {
  if (isCowImageCurrent.value)
    AnnotatableBodyPartDisplayParamsStoreManager.store.switchToNextSelected();
}

const switchToPreviousSpawnableAnnotation = () => {
  if (isCowImageCurrent.value)
    AnnotatableBodyPartDisplayParamsStoreManager.store.switchToPreviousSelected();
}

// ------------------------------
//  Annotation
// ------------------------------

const displayAnnotationsHolder = ref(true);

const spawnDotAnnotation = (annotatableBodyPartDisplayParamsData: SAnnotatableBodyPartDisplayParamsData) =>
{
  const x = cursorOnOverlayX.value;
  const y = cursorOnOverlayY.value;

  const currentBoundingBox = AnnotatableImagesStoreManager.store.currentBoundingBox;

  if (
      x && y
      && currentBoundingBox
      && currentBoundingBox.instanceId in cursorTrackingByBoundingBox
      && cursorTrackingByBoundingBox[currentBoundingBox.instanceId]
      && isCowImageCurrent.value
  )
  {
    const boundingBoxManager = new Spatial2DBoundingBoxManager(annotatableBodyPartDisplayParamsData.value);

    const result = boundingBoxManager.addDotAnnotation(currentBoundingBox, {
      instanceId: AbstractSubStoreManager.createInstanceId(),
      left: x * 100 / overlay.value.offsetWidth,
      top: y * 100 / overlay.value.offsetHeight,
      type: annotatableBodyPartDisplayParamsData.value,
      colorCode: annotatableBodyPartDisplayParamsData.colorCode
    });

    if (Array.isArray(result))
      throw "Couldn't create annotation. Errors : " + result.join(", ");

    // Doing this forces the dot annotations' re-render and is being done invisibly using a transition
    displayAnnotationsHolder.value = false;
    setTimeout(() => {
      displayAnnotationsHolder.value = true;
    }, 5);
  }
}


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "image-overlay",
    id: 'image-overlay-' + __props.annotatableImage.image.instanceId,
    ref_key: "overlay",
    ref: overlay
  }, [
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.annotatableImage.boundingBoxes, (boundingBox) => {
      return (_openBlock(), _createBlock(Spatial2DBoundingBox, {
        key: boundingBox.instanceId,
        "parent-instance-id": __props.annotatableImage.instanceId,
        "bounding-box": boundingBox,
        "parent-overlay-ref": overlay.value,
        "is-selected": _unref(isBoundingBoxCurrent)(boundingBox.instanceId),
        "display-annotations-holder": displayAnnotationsHolder.value,
        onOnBoundingBoxClicked: setCurrentBoundingBox,
        onOnCursorEntersBoundingBox: onCursorEntersCurrentBoundingBox,
        onOnCursorLeavesBoundingBox: onCursorLeavesCurrentBoundingBox,
        onSpawnNewDotAnnotation: spawnDotAnnotation
      }, null, 8, ["parent-instance-id", "bounding-box", "parent-overlay-ref", "is-selected", "display-annotations-holder"]))
    }), 128))
  ], 8, _hoisted_1))
}
}

})